import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  ActivityItem,
  CreateOriginFields,
  ShippingCaseResponse,
  SupplierType,
} from "@pages/shippingView/types";
import { CircularProgress, Stack } from "@mui/material";
import Warning from "@atoms/warning";
import { RootState, useAppDispatch } from "@store/index";
import {
  updateFreightCharge,
  fetchActivitiesByRoute,
  hideWarning,
} from "@pages/shippingView/shippingSlice";
import { prepareEditingState } from "@pages/shippingView/helpers/prepare-accordion-state";
import OriginAccordion from "@pages/shippingView/components/originAccordion";
import BlankShippingCase from "@pages/shippingView/components/blankShippingCase";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useCancelForm } from "@context/cancel-form-context";
import CancelDialog from "@pages/shippingView/components/cancelDialog";

const Freight: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { oid, sid } = useParams();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [editingIds, setEditingIds] = useState<Set<number>>(new Set());
  const [expandedAccordions, setExpandedAccordions] = useState<number[]>([]);
  const [lastEditedId, setLastEditedId] = useState<number | null>(null);
  const [editDataMap, setEditDataMap] = useState<
    Record<number, CreateOriginFields & { supplier: SupplierType }>
  >({});

  const { shipping } = useOutletContext<{ shipping: ShippingCaseResponse }>();
  const { loading, items, success, error } = useSelector(
    (state: RootState) => state.viewShipping.shipping.freight,
  );

  const {
    setPromptElement,
    hidePrompt,
    setIsDirty,
    allowNavigation,
    setActiveScope,
  } = useCancelForm();

  useEffect(() => {
    setPromptElement?.(
      <CancelDialog
        type="freight"
        edit={true}
        handleClose={() => {
          hidePrompt("freight");
        }}
        handleCancel={() => {
          hidePrompt("freight");
        }}
      />,
      "freight",
    );
  }, [setPromptElement, hidePrompt]);

  useEffect(() => {
    setActivities(items);

    const result = prepareEditingState(
      items,
      editDataMap,
      editingIds,
      expandedAccordions,
      lastEditedId,
    );

    setEditingIds(result.editingIds);
    setEditDataMap(result.editDataMap);
    setExpandedAccordions(result.expandedAccordions);

    if (lastEditedId !== null) {
      setEditingIds((prev) => {
        const copy = new Set(prev);
        copy.delete(lastEditedId);
        return copy;
      });
      setLastEditedId(null);
    }
  }, [items]);

  useEffect(() => {
    dispatch(fetchActivitiesByRoute(shipping.id, "FREIGHT"));
  }, [shipping.id]);

  const toggleAccordion = (id: number) => {
    setExpandedAccordions((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleEdit = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number,
  ) => {
    e.stopPropagation();
    const item = activities.find((i) => i.id === id);
    if (item) {
      setExpandedAccordions((prev) =>
        prev.includes(id) ? prev : [...prev, id],
      );
      setEditingIds((prev) => new Set(prev).add(id));
      setEditDataMap((prev) => ({
        ...prev,
        [id]: {
          supplier: item.supplier,
          supplier_id: item.supplier?.id as number,
          execution_date: item.execution_date || null,
          completion_date: item.completion_date || null,
          comment: item.comment || "",
        },
      }));
    }
  };

  const updateFields = (id: number, values: Partial<CreateOriginFields>) => {
    setActiveScope("freight");
    setIsDirty(true);
    setEditDataMap((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...values,
      },
    }));
  };

  const handleCancel = (id: number) => {
    setEditingIds((prev) => {
      const copy = new Set(prev);
      copy.delete(id);
      return copy;
    });
    setEditDataMap((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const handleSave = (id: number) => {
    allowNavigation?.();
    setLastEditedId(id);
    const data = editDataMap[id];
    if (data) {
      dispatch(updateFreightCharge(data, shipping.id, id, "FREIGHT"));
    }
  };

  const closeCallback = () => dispatch(hideWarning("freight"));
  return (
    <>
      {!!error && (
        <Warning text={error} severity="error" closeCallback={closeCallback} />
      )}
      {!!success && (
        <Warning
          text={success}
          severity="success"
          closeCallback={closeCallback}
        />
      )}
      {loading && <CircularProgress />}
      {!loading && !items?.length && (
        <BlankShippingCase
          type="freight"
          text="No freight services found."
          subtext="To get started, please click the button below."
          init={() =>
            navigate(`/offers/${oid}/shipping-case/${sid}/key-information`)
          }
        />
      )}
      {!loading && items && !!items.length && (
        <Stack gap={1}>
          {activities.map((item, index) => {
            const isEditing = editingIds.has(item.id);
            const fields = editDataMap[item.id];

            return (
              <OriginAccordion
                key={item.id}
                type="freight"
                item={item}
                index={index}
                expandedAccordions={expandedAccordions}
                isEditing={isEditing}
                fields={fields}
                editDataMap={editDataMap}
                toggleAccordion={toggleAccordion}
                handleEdit={handleEdit}
                updateFields={updateFields}
                handleCancelCallback={handleCancel}
                handleSave={handleSave}
              />
            );
          })}
        </Stack>
      )}
    </>
  );
};

export default Freight;
